import React from "react";
import { t } from "ttag";

import { money } from "../../../utils/format";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { IBasketLine } from "../models";
import { setBasketSearchTerm, viewStackPush } from "../reducers";
import {
    lookupBasket,
    startAssistingCustomer,
    startNewBasket,
    stopAssistingCustomer,
} from "../thunks";
import { getBasketTotal } from "../utils";
import { BasketAssign } from "./BasketAssign";
import { CustomerDetails } from "./CustomerDetails";

const buildLines = (lines: readonly IBasketLine[]): React.ReactNode => {
    if (lines.length <= 0) {
        return (
            <tr>
                <td colSpan={7}>
                    <em>{t`Basket is empty`}</em>
                </td>
            </tr>
        );
    }
    return lines.map((line) => {
        return (
            <tr key={line.id}>
                <td>{line.id}</td>
                <td>{line.product.title}</td>
                <td>{line.quantity}</td>
                <td>{money(line.price_excl_tax)}</td>
                <td></td>
            </tr>
        );
    });
};

export const BasketSearch = (): React.ReactNode => {
    const dispatch = useAppDispatch();
    const basketSearch = useAppSelector((state) => state.csr.basketSearch);
    const assisting = useAppSelector((state) => state.csr.assisting);

    const searchRef = React.useRef<HTMLInputElement>(null);
    const hasMountedRef = React.useRef(false);

    React.useEffect(() => {
        // If no term is entered and the CSR is already assisting a basket, default to it.
        if (
            !basketSearch.term &&
            assisting.basket &&
            assisting.basket.encoded_basket_id
        ) {
            dispatch(setBasketSearchTerm(assisting.basket.encoded_basket_id));
            void dispatch(lookupBasket(assisting.basket.encoded_basket_id));
            return;
        }
        // Otherwise, do the lookup based on the search bar
        void dispatch(lookupBasket(basketSearch.term));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        if (searchRef.current) {
            searchRef.current.focus();
        }
    });

    const onSearchTermChange = (
        event: React.FormEvent<HTMLInputElement>,
    ): void => {
        dispatch(setBasketSearchTerm(event.currentTarget.value));
    };

    const onDoSearch = (event: React.FormEvent<HTMLElement>): void => {
        event.preventDefault();
        if (searchRef.current) {
            void dispatch(lookupBasket(searchRef.current.value));
        }
    };

    const onStartNewBasket = (event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        void dispatch(startNewBasket());
    };

    const onViewCustomerDetails = (
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        event.preventDefault();
        const owner = basketSearch.owner;
        if (!owner) {
            return;
        }
        const component = <CustomerDetails url={owner.url} />;
        dispatch(viewStackPush({ name: t`Customer Details`, component }));
    };

    const onAssignBasket = (event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        const basket = basketSearch.basket;
        if (!basket) {
            return;
        }
        const component = (
            <BasketAssign
                basketID={basket.id}
                email={assisting.customer ? assisting.customer.email : ""}
            />
        );
        dispatch(
            viewStackPush({ name: t`Assign Basket to Customer`, component }),
        );
    };

    const onStartAssisting = (event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        const owner = basketSearch.owner;
        if (!owner) {
            return;
        }
        dispatch(startAssistingCustomer(owner.url))
            .unwrap()
            .then(() => {
                onDoSearch(event);
                return null;
            })
            .catch((err) => {
                console.error(err);
                alert(t`Error! Could not start assisting customer.`);
            });
    };

    const onStopAssisting = (event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        const owner = basketSearch.owner;
        if (!owner) {
            return;
        }
        dispatch(stopAssistingCustomer(owner.url))
            .unwrap()
            .then(() => {
                onDoSearch(event);
                return null;
            })
            .catch((err) => {
                console.error(err);
                alert(t`Error! Could not stop assisting customer.`);
            });
    };

    const renderResults = (): React.ReactNode => {
        const newBasketHelpText = assisting.customer
            ? t`Freeze the current basket and start a new basket. Since you are currently assisting a customer, the new basket will be owned by and immediately visible to them.`
            : t`Freeze the current basket and start a new basket. Since you are not currently assisting a customer, the new basket will be owned by you. You will have the option to assign the basket to a customer later.`;
        const buttonsTop = (
            <div className="button-group">
                <button
                    onClick={onStartNewBasket}
                >{t`Start New Basket`}</button>
                <p className="button-group__help">{newBasketHelpText}</p>
            </div>
        );

        const basket = basketSearch.basket;
        const owner = basketSearch.owner;
        if (!basket || !owner || basketSearch.error) {
            return (
                <div className="csr-customer-basket">
                    <div className="csr-customer-basket__header">
                        <h2>{t`Actions`}</h2>
                        {buttonsTop}
                        <h2>{basketSearch.error}</h2>
                    </div>
                </div>
            );
        }

        let assistHelpText: string;
        if (owner.is_assisting) {
            assistHelpText = t`Return to editing your own basket by clicking "Stop Assisting Customer"`;
        } else if (assisting.customer) {
            assistHelpText = t`Click "Start Assisting Customer" to begin editing his or her basket. This will end your currently active customer assistance session.`;
        } else {
            assistHelpText = t`Click "Start Assisting Customer" to begin editing his or her basket.`;
        }
        const buttonsBottom = (
            <div className="button-group" key="view-customer-details">
                <div>
                    <button
                        title={t`View details about this customer.`}
                        onClick={onViewCustomerDetails}
                    >
                        {t`View Customer Details`}
                    </button>
                </div>
                <div>
                    <button
                        title={t`Start assisting this customer. While assisting a customer, all changes affect the customer's basket rather than your own basket.`}
                        onClick={onStartAssisting}
                        disabled={owner.is_assisting}
                    >
                        {t`Start Assisting Customer`}
                    </button>
                    <button
                        title={t`Stop assisting this customer. You will be returned to viewing and editing your own basket, rather than the customer's basket.`}
                        onClick={onStopAssisting}
                        disabled={!owner.is_assisting}
                    >
                        {t`Stop Assisting Customer`}
                    </button>
                    <p className="button-group__help">{assistHelpText}</p>
                </div>
                <div>
                    <button
                        title={t`Email this basket to a customer.`}
                        onClick={onAssignBasket}
                        disabled={
                            !owner.is_assisting ||
                            !basket ||
                            basket.status !== "Open"
                        }
                    >
                        {t`Send Basket to Customer`}
                    </button>
                    <p className="button-group__help">
                        {t`Assign this basket to a customer and send it to them via email.`}
                    </p>
                </div>
            </div>
        );

        const created = basket.date_created
            ? new Date(basket.date_created).toLocaleString()
            : "-";
        const merged = basket.date_merged
            ? new Date(basket.date_merged).toLocaleString()
            : "-";
        const submitted = basket.date_submitted
            ? new Date(basket.date_submitted).toLocaleString()
            : "-";
        const totalExclTax = getBasketTotal(basket);
        const groups = owner.groups || [];
        const assistor = owner.currently_assisted_by;
        const currentAssistor = assistor
            ? `${assistor.first_name} ${assistor.last_name} <${assistor.email}>`
            : null;
        return (
            <div className="csr-customer-basket">
                <div className="csr-customer-basket__header">
                    <h2>{t`Actions`}</h2>
                    {buttonsTop}
                    <h2>
                        {t`Basket:`} <em>{basket.encoded_basket_id}</em>
                    </h2>
                </div>
                <div className="csr-customer-basket__basic">
                    <table>
                        <tbody>
                            <tr>
                                <th>{t`First Name`}</th>
                                <td>{owner.first_name || "-"}</td>
                            </tr>
                            <tr>
                                <th>{t`Last Name`}</th>
                                <td>{owner.last_name || "-"}</td>
                            </tr>
                            <tr>
                                <th>{t`Username`}</th>
                                <td>{owner.username || "-"}</td>
                            </tr>
                            <tr>
                                <th>{t`Email Address`}</th>
                                <td>{owner.email || "-"}</td>
                            </tr>
                            <tr>
                                <th>{t`Groups`}</th>
                                <td>
                                    {groups.length <= 0
                                        ? "-"
                                        : groups.map((name) => {
                                              return (
                                                  <span
                                                      className="csr-customer-group"
                                                      key={name}
                                                  >
                                                      {name}
                                                  </span>
                                              );
                                          })}
                                </td>
                            </tr>
                            <tr>
                                <th>{t`Currently being assisted by`}</th>
                                <td>{currentAssistor || "-"}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="csr-customer-basket__basic">
                    <table>
                        <tbody>
                            <tr>
                                <th>{t`Status`}</th>
                                <td>{basket.status}</td>
                            </tr>
                            <tr>
                                <th>{t`Total (excl. Tax)`}</th>
                                <td>{money(totalExclTax)}</td>
                            </tr>
                            <tr>
                                <th>{t`Date Created`}</th>
                                <td>{created}</td>
                            </tr>
                            <tr>
                                <th>{t`Date Merged`}</th>
                                <td>{merged}</td>
                            </tr>
                            <tr>
                                <th>{t`Date Submitted`}</th>
                                <td>{submitted}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="csr-customer-basket__lines">
                    {buttonsBottom}
                    <h2>{t`Lines`}</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>{t`Line ID`}</th>
                                <th>{t`Product`}</th>
                                <th>{t`Options`}</th>
                                <th>{t`Quantity`}</th>
                                <th>{t`Line Price (excl. Tax)`}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>{buildLines(basket.lines)}</tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="csr-basket-search">
            <form onSubmit={onDoSearch}>
                <p
                    className="csr-basket-search__help"
                    dangerouslySetInnerHTML={{
                        __html: t`Search for a customer's cart using their <em>Cart Reference Number</em>. For example: <em>972-1C2-3</em>.`,
                    }}
                ></p>
                <div className="csr-basket-search__input">
                    <input
                        type="search"
                        placeholder={t`Search`}
                        ref={searchRef}
                        value={basketSearch.term}
                        onChange={onSearchTermChange}
                    />
                </div>
                <div className="csr-basket-search__button">
                    <button type="submit">{t`Lookup`}</button>
                </div>
            </form>
            <div className="csr-basket-search__results">{renderResults()}</div>
        </div>
    );
};
